import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import BrandLogo from '@/components/BrandLogo';
import { 
  Monitor, LogIn, ArrowRight, CheckCircle, 
  Flame, BarChart3, Shield, MessageSquare, 
  Database, Award, Users, Building2, 
  ClipboardCheck, FileText, Menu, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_SOLUTION_CONTENT = {
  main_heading: 'The solution',
  main_description:
    'The system optimizes healthcare staff training, knowledge, cost-efficiency and compliance in healthcare settings. It monitors performance, tracks savings, and identifies training needs for enhanced management.',
  staff_skills_title: 'Enhancing staff skills and compliance for operational excellence',
  staff_skills_description:
    'Empower your teams with role-ready learning, compliance confidence, and measurable performance gains.',
  htms_title: 'Healthcare training management system',
  htms_content: 'Enhanced staff skills for optimal care',
  htms_right_image_url:
    'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=500&fit=crop',
  htms_right_media_type: 'image',
  htms_right_media_s3_key: '',
  htms_right_media_url:
    'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=500&fit=crop',
  htms_supporting_description:
    'HTMS provides structured, standards-aligned training workflows for hospitals and healthcare teams.',
  courses_section_title: 'Examples of courses included',
  courses_section_description:
    'Comprehensive training modules covering all aspects of healthcare operations',
  feature_points: [
    'HTMS is a smart, standards-compliant platform that trains staff and ensures adherence to national (NABH) standards and international benchmarks.',
    'By improving operational efficiency and delivering superior patient outcomes, HTMS empowers healthcare providers to uphold the highest standards of care.',
    'AI-powered chat assistant provides instant guidance on healthcare processes and compliance, making learning seamless, intuitive, and interactive.'
  ],
  cards: [
    {
      card_id: 'solution_card_1',
      icon_key: 'flame',
      title: 'Use of fire extinguishers',
      description:
        'Understanding and applying correct procedures to safely operate fire extinguishers to control small fires, ensuring safety of patients, staff, and facilities.'
    },
    {
      card_id: 'solution_card_2',
      icon_key: 'bar_chart',
      title: 'Key performance indicators',
      description:
        'Specific, measurable metrics to evaluate performance, efficiency, and quality of services, guiding improvements and ensuring alignment with institutional goals.'
    },
    {
      card_id: 'solution_card_3',
      icon_key: 'shield',
      title: 'Safe usage of radioactive materials',
      description:
        'Protocols ensuring safe handling, administration, and disposal of radioactive materials, minimizing exposure risks to patients, staff, and environment.'
    },
    {
      card_id: 'solution_card_4',
      icon_key: 'message',
      title: 'Communication skills',
      description:
        'Clearly and effectively exchange information with patients, families, and healthcare teams, ensuring understanding, collaboration, and optimal patient care.'
    },
    {
      card_id: 'solution_card_5',
      icon_key: 'database',
      title: 'Information management system',
      description:
        'Collection, storage, management, and analysis of patient and operational data, facilitating efficient decision-making and improving patient care.'
    },
    {
      card_id: 'solution_card_6',
      icon_key: 'award',
      title: 'Credentialing and privileging',
      description:
        'Verifying qualifications, skills, and experience of healthcare providers and granting specific privileges to ensure safe and competent patient care.'
    },
    {
      card_id: 'solution_card_7',
      icon_key: 'users',
      title: 'Human resource management',
      description:
        'Recruiting, training, and retaining staff, managing employee relations, ensuring compliance with labor laws, and fostering a supportive work environment.'
    },
    {
      card_id: 'solution_card_8',
      icon_key: 'building',
      title: 'Facility management and safety',
      description:
        'Maintenance and operation of physical environment, including emergency plans, medical equipment management, ensuring everything is safe, clean, and functional.'
    },
    {
      card_id: 'solution_card_9',
      icon_key: 'clipboard',
      title: 'Responsibility of management',
      description:
        'Overseeing operations, ensuring quality patient care, managing finances, complying with regulations, and fostering a positive work environment.'
    },
    {
      card_id: 'solution_card_10',
      icon_key: 'file_text',
      title: 'Clinical audit',
      description:
        'Review and evaluation of clinical practices and patient care against established standards to identify areas for improvement and ensure high-quality outcomes.'
    }
  ]
};

const SolutionsPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [solutionContent, setSolutionContent] = useState(DEFAULT_SOLUTION_CONTENT);

  useEffect(() => {
    const fetchSolutionContent = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cms/solution-page`);
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        if (data?.content) {
          const mediaUrl = data.content.htms_right_media_url || data.content.htms_right_image_url || DEFAULT_SOLUTION_CONTENT.htms_right_media_url;
          setSolutionContent({
            ...DEFAULT_SOLUTION_CONTENT,
            ...data.content,
            htms_right_media_url: mediaUrl,
            htms_right_media_type: data.content.htms_right_media_type === 'video' ? 'video' : 'image',
            cards: Array.isArray(data.content.cards) && data.content.cards.length > 0
              ? data.content.cards
              : DEFAULT_SOLUTION_CONTENT.cards,
            feature_points: Array.isArray(data.content.feature_points) && data.content.feature_points.length > 0
              ? data.content.feature_points
              : DEFAULT_SOLUTION_CONTENT.feature_points,
          });
        }
      } catch (error) {
        console.error('Failed to fetch solution page CMS content:', error);
      }
    };

    fetchSolutionContent();
  }, []);

  const iconMap = {
    flame: { Icon: Flame, className: 'text-red-500' },
    bar_chart: { Icon: BarChart3, className: 'text-blue-600' },
    shield: { Icon: Shield, className: 'text-purple-600' },
    message: { Icon: MessageSquare, className: 'text-green-600' },
    database: { Icon: Database, className: 'text-cyan-600' },
    award: { Icon: Award, className: 'text-yellow-600' },
    users: { Icon: Users, className: 'text-indigo-600' },
    building: { Icon: Building2, className: 'text-orange-600' },
    clipboard: { Icon: ClipboardCheck, className: 'text-pink-600' },
    file_text: { Icon: FileText, className: 'text-teal-600' }
  };

  const renderCardVisual = (card) => {
    const imageUrl = (card.image_url || '').trim();
    if (imageUrl) {
      return (
        <img
          src={imageUrl}
          alt={card.title || 'Course visual'}
          className="w-12 h-12 rounded-lg object-cover"
        />
      );
    }

    const selected = iconMap[(card.icon_key || '').trim()] || iconMap.file_text;
    const IconComponent = selected.Icon;
    return <IconComponent className={`w-12 h-12 ${selected.className}`} />;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <BrandLogo />
            </div>

            {/* Desktop Navigation & Buttons - All aligned right with equal spacing */}
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => navigate('/')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Home
              </button>
              <button onClick={() => navigate('/solutions')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-semibold transition-colors border-b-2 border-[#0056B3]">
                The solution
              </button>
              <button onClick={() => navigate('/pricing')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Pricing
              </button>
              <button onClick={() => navigate('/contact')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Contact us
              </button>
              <Button 
                onClick={() => navigate('/request-demo')}
                className="bg-[#10B981] hover:bg-[#059669] text-white text-sm px-4 py-2 rounded-lg transition-colors"
              >
                <Monitor className="w-4 h-4 mr-2" />
                Request for demo
              </Button>
              <Button 
                onClick={() => navigate('/signin')}
                className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white text-sm px-4 py-2 rounded-lg transition-colors"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign in
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-gray-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <nav className="flex flex-col space-y-3">
                <button onClick={() => navigate('/')} className="text-sm text-gray-700 hover:text-[#0056B3] font-medium text-left">
                  Home
                </button>
                <button onClick={() => navigate('/solutions')} className="text-sm text-[#0056B3] font-semibold text-left">
                  The solution
                </button>
                <button onClick={() => navigate('/pricing')} className="text-sm text-gray-700 hover:text-[#0056B3] font-medium text-left">
                  Pricing
                </button>
                <button onClick={() => navigate('/contact')} className="text-sm text-gray-700 hover:text-[#0056B3] font-medium text-left">
                  Contact us
                </button>
                <Button 
                  onClick={() => navigate('/request-demo')}
                  className="bg-[#10B981] hover:bg-[#059669] text-white text-sm w-full justify-center"
                >
                  Request for demo
                </Button>
                <Button 
                  onClick={() => navigate('/signin')}
                  className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white text-sm w-full justify-center"
                >
                  Sign in
                </Button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Subchapter */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <p className="text-sm md:text-base font-semibold text-[#0056B3] tracking-wide uppercase mb-4">
              {solutionContent.main_heading}
            </p>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              {solutionContent.staff_skills_title}
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {solutionContent.main_description}
            </p>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              {solutionContent.staff_skills_description}
            </p>
            <Button 
              onClick={() => navigate('/signup')}
              className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white text-lg px-8 py-6 rounded-lg"
            >
              Get started
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Platform Details Subchapter */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {solutionContent.htms_title}
            </h2>
            <p className="text-lg text-gray-600">
              {solutionContent.htms_content}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            {/* Left: Text Content */}
            <div>
              <div className="space-y-6">
                {(solutionContent.feature_points || []).map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <p className="text-gray-700 leading-relaxed">{feature}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Image with Creative Frame */}
            <div className="relative flex items-center justify-center">
              {/* Background decorative shapes */}
              <div className="absolute -top-4 -right-4 w-full h-full bg-gradient-to-br from-[#0056B3]/20 to-[#00AEEF]/20 rounded-2xl transform rotate-3"></div>
              <div className="absolute -bottom-4 -left-4 w-full h-full bg-gradient-to-br from-[#00AEEF]/15 to-[#0056B3]/15 rounded-2xl transform -rotate-3"></div>
              
              {/* Main image container */}
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                  {solutionContent.htms_right_media_type === 'video' ? (
                    <video
                      src={solutionContent.htms_right_media_url || solutionContent.htms_right_image_url || DEFAULT_SOLUTION_CONTENT.htms_right_media_url}
                      controls
                      className="w-full h-auto object-cover"
                      style={{ maxHeight: '420px' }}
                    />
                  ) : (
                    <img
                      src={solutionContent.htms_right_media_url || solutionContent.htms_right_image_url || DEFAULT_SOLUTION_CONTENT.htms_right_media_url}
                      alt="Healthcare Training"
                      className="w-full h-auto object-cover"
                      style={{ maxHeight: '420px' }}
                    />
                  )}
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0056B3]/30 to-transparent"></div>
              </div>
              
              {/* Floating accent elements */}
              <div className="absolute -top-6 -left-6 w-16 h-16 bg-[#0056B3] rounded-full flex items-center justify-center shadow-lg z-20">
                <Users className="w-8 h-8 text-white" />
              </div>
              <div className="absolute -bottom-6 -right-6 w-16 h-16 bg-[#00AEEF] rounded-full flex items-center justify-center shadow-lg z-20">
                <ClipboardCheck className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>

          <p className="text-center text-gray-600 max-w-3xl mx-auto text-lg leading-relaxed">
            {solutionContent.htms_supporting_description}
          </p>
        </div>
      </section>

      {/* Courses Subchapter */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {solutionContent.courses_section_title}
            </h2>
            <p className="text-xl text-gray-600">
              {solutionContent.courses_section_description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(solutionContent.cards || []).map((card, index) => (
              <div 
                key={card.card_id || index}
                className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
              >
                <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300">
                  {renderCardVisual(card)}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#0056B3] transition-colors">
                  {card.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {card.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Subchapter */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#0056B3] to-[#00AEEF] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to transform your healthcare training?
          </h2>
          <p className="text-xl mb-10 opacity-90">
            Join leading healthcare institutions that trust our platform for comprehensive staff training and compliance management.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={() => navigate('/signup')}
              className="bg-white text-[#0056B3] hover:bg-gray-100 text-lg px-8 py-6 rounded-lg"
            >
              Get started now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              onClick={() => navigate('/request-demo')}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 rounded-lg"
            >
              <Monitor className="w-5 h-5 mr-2" />
              Request a demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SolutionsPage;
